import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditAudioFileMutation } from "@modules/audioFiles/services/mutations";
import { AudioFileFormValues, audiofileSchema } from "@modules/audioFiles/validations";
import { listAudioFilesQueryKey } from "@modules/audioFiles/services/queries/listAudioFiles/query";

export function useEditAudioFileModal(modalRef: RefObject<ModalRef>, onComplete?: () => void) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<AudioFileFormValues>({
    resolver: zodResolver(audiofileSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditAudioFileMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listAudioFilesQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("v1/audiobook_admin.success.edit"),
      });

      onComplete?.();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: AudioFileFormValues) => {
    const audiofileId = modalRef.current?.getId();
    await mutateAsync({ id: audiofileId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
